"use strict"

import { Suspense, createContext, forwardRef, lazy, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import * as Sentry from "@sentry/react";
import ChatPageHeader from "../../chat-sidebar/ChatPageHeader";
import DiagnoseChat from "../../../pages/diagnose-chat";
import NotFound from "../../../pages/not-found";
import DisclaimerPopup from "../../disclaimer-popup"
import PageLoading from "@minohealth/common/components/page.loading/PageLoading";
import { useSelector } from "react-redux";
import AppNavigationContext from "@minohealth/common/services/AppNavigationContext";
import { SESSION_TIMEOUT_EVENT } from "@minohealth/common/services/ApiService";
import { useForceLogoutUser } from "@minohealth/common/hooks/useForceLogoutUser";
import { FeatureFlags } from "@minohealth/common/lib/feature-flags";

import { IoMdClose } from "react-icons/io";

import "./chat-layout.css"

// Wraps lazy() to auto-reload once when a JS chunk can't be fetched (stale deployment).
// Uses sessionStorage to prevent infinite reload loops.
function lazyWithRetry(factory) {
  return lazy(async () => {
    const storageKey = 'chunk-load-force-refreshed';
    const alreadyRefreshed = JSON.parse(sessionStorage.getItem(storageKey) || 'false');
    try {
      const mod = await factory();
      sessionStorage.setItem(storageKey, 'false');
      return mod;
    } catch (err) {
      if (!alreadyRefreshed) {
        sessionStorage.setItem(storageKey, 'true');
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    }
  });
}

const AccountSettings = lazyWithRetry(() => import("@minohealth/common/pages/account/AccountSettings"));
const ApiRequest = lazyWithRetry(() => import("@minohealth/common/pages/api-request/ApiRequest"));
const ReportReviewerForm = lazyWithRetry(() => import("../../report-reviewer-form"));
const LoginFormLayout = lazyWithRetry(() => import("@minohealth/common/pages/login/LoginFormLayout"));
const RegisterFormLayout = lazyWithRetry(() => import("@minohealth/common/pages/register/RegisterFormLayout"));
const VerifyOTPFormLayout = lazyWithRetry(() => import("@minohealth/common/pages/otp_verification/VerifyOTPFormLayout"));
const PasswordRecoveryFormLayout = lazyWithRetry(() => import("@minohealth/common/pages/recovery/PasswordRecoveryFormLayout"));
const RecoverySummary = lazyWithRetry(() => import("@minohealth/common/pages/recovery/extras/RecoverySummary"));
const UserCategorisationModal = lazyWithRetry(() => import("@minohealth/common/components/user-categorisation"));
const Payment = lazyWithRetry(() => import("@minohealth/common/pages/payment/Payment"));
const ReviewerApplications = lazyWithRetry(() => import("../../../pages/reviewer-applications"));
const PromoCodes = lazyWithRetry(() => import("../../../pages/promo-codes"));
const BioTools = lazyWithRetry(() => import("../../../pages/bio-tools"));
const AcceptMembershipPage = lazyWithRetry(() => import("@minohealth/common/pages/accept-membership/AcceptMembershipPage"));
const MedscribeConnect = lazyWithRetry(() => import("../../../pages/medscribe-connect"));

const chatAuthContext = createContext({
  /** @type {() => Promise<*>} */
  authenticate() { },
})

export function useChatAuthContext() {
  return useContext(chatAuthContext)
}

/**
  * @module containers/ChatLayout
  */

/**
 * Valid account route actions — mirrors the tabRoutes defined in AccountSettings
 * (@minohealth/common/src/pages/account/AccountSettings.jsx).
 * When a new tab is added there, add its action name here too.
 */
const VALID_ACCOUNT_ACTIONS = [
  "edit",
  "addMembers",
  "changepassword",
  "topup",
  "statements",
  "reviewer-payouts",
  "api-keys",
];

const chatLinks = {
  LOGIN: `/login`,
  REGISTER: `/register`,
  VERIFY_OTP: `/verify-otp`,
  FORGOT_PASSWORD: `/recovery`,
  FORGOT_PASSWORD_SUMMARY: `/summary`,
  ACCOUNT: `/account/:action(${VALID_ACCOUNT_ACTIONS.join("|")})`,
  API_REQUEST: `/api-request`,
  PAYMENT: `/payment/:action`,
  REVIEWER_APPLICATIONS: `/reviewer-applications`,
  REVIEWER_REGISTRATION: `/reviewer-registration`,
  PROMO_CODES: `/promo-codes`,
  BIO_TOOLS: `/bio-tools`,
  ACCEPT_MEMBERSHIP: `/accept-membership`,
  MEDSCRIBE_CONNECT: `/medscribe/connect`,
}

/**
 * Routes that open as a modal over the main area. The main area stays empty for
 * them; a Not Found page behind would show when Checkout hides the modal for Paystack.
 */
const MODAL_ROUTES = [
  chatLinks.LOGIN,
  chatLinks.REGISTER,
  chatLinks.VERIFY_OTP,
  chatLinks.FORGOT_PASSWORD,
  chatLinks.FORGOT_PASSWORD_SUMMARY,
  chatLinks.ACCOUNT,
  chatLinks.API_REQUEST,
];

/**
  * A layout component that wraps the chat part of the app
  *
  * @param {RouteComponentProps} props The route props
  * @returns {React.ReactNode}
  *
  * @function
  */
export default function ChatLayout({ history, match }) {
  const { setAppNavHistory } = useContext(AppNavigationContext);
  const { user } = useSelector(state => state.app.datastate)
  const logoutUser = useForceLogoutUser()
  const isLoggedIn = user?._id != null

  useEffect(() => {
    setAppNavHistory(history);
  }, []);

  useEffect(() => {
    if (user?._id == null) return

    function handleSessionTimeout() {
      logoutUser(chatLinks.LOGIN);
    }

    document.addEventListener(SESSION_TIMEOUT_EVENT, handleSessionTimeout)
    return () => document.removeEventListener(SESSION_TIMEOUT_EVENT, handleSessionTimeout)
  }, [user?._id, logoutUser])

  /** @type {React.RefObject<{resolve:()=>void, reject:()=>void, controller: AbortController} | null>} */
  const authPromiseRef = useRef(null)

  useEffect(() => {
    if (!match.isExact) return
    completeAuthentication()
  }, [match])

  useEffect(() => {
    return () => {
      authPromiseRef.current?.controller?.abort()
    }
  }, [])

  function completeAuthentication() {
    const isAuthSuccessful = user?._id != null
    if (isAuthSuccessful) authPromiseRef.current?.resolve?.()
    else authPromiseRef.current?.reject?.()

    authPromiseRef.current = null
  }

  function startAuthentication() {
    history.push(chatLinks.LOGIN)
  }

  function authenticate() {
    return new Promise((resolve, reject) => {
      const controller = new AbortController()
      authPromiseRef.current = { resolve, reject, controller }
      startAuthentication()

      controller.signal.addEventListener("abort", () => {
        try { controller.signal.throwIfAborted() }
        catch (err) {
          Sentry.captureException(err, {
            extra: {
              context: 'ChatLayout - authenticate signal abort',
              isLoggedIn,
              userId: user?._id || 'undefined',
              userRole: user?.role || 'undefined',
              isExactMatch: match?.isExact || false
            }
          });
          reject(err)
        }
      }, { once: true })
    })
  }

  const { hasDiscardedDisclaimer } = useSelector((state) => state.app.uistate);
  const [canShowDisclaimer, setCanShowDisclaimer] = useState(false)
  const hasAlreadyShownDisclaimerRef = useRef(false)
  useEffect(() => {
    if (user?._id == null) return
    if (user?.role == null) return
    if (hasAlreadyShownDisclaimerRef.current) return
    if (hasDiscardedDisclaimer) return
    if (!match.isExact) return // Only show if we are on the home route
    setCanShowDisclaimer(true)
    hasAlreadyShownDisclaimerRef.current = true
  }, [hasDiscardedDisclaimer, user?._id, user?.role, match.isExact])

  useEffect(() => {
    if (!isLoggedIn) return
    return () => {
      hasAlreadyShownDisclaimerRef.current = false
    }
  }, [isLoggedIn])

  const location = useHistory().location;
  const isChatPage = location.pathname === "/" || location.pathname.startsWith("/anokye") || location.pathname.startsWith("/mopacs");

  return (
    <chatAuthContext.Provider value={{ authenticate }}>
      <div className="app-container fill">
        {!isChatPage && <ChatPageHeader variant="app" />}

        <main className="app-content">
          <Suspense fallback={<PageLoading />}>
            <Switch>

              <Route path={chatLinks.PAYMENT} >
                <Suspense fallback={<PageLoading />}>
                  <Payment history={history} />
                </Suspense>
              </Route>
              {isAdmin(user) && (
                <Route path={chatLinks.REVIEWER_APPLICATIONS} >
                  <Suspense fallback={<PageLoading />}>
                    <ReviewerApplications />
                  </Suspense>
                </Route>
              )}
              {isPromoAdmin(user) && FeatureFlags.PROMOCODES && (
                <Route path={chatLinks.PROMO_CODES}>
                  <Suspense fallback={<PageLoading />}>
                    <PromoCodes />
                  </Suspense>
                </Route>
              )}
              {isAdmin(user) && (
                <Route path={chatLinks.BIO_TOOLS}>
                  <Suspense fallback={<PageLoading />}>
                    <BioTools />
                  </Suspense>
                </Route>
              )}

              <Route path="/anokye" >
                <DiagnoseChat />
              </Route>
              <Route path="/mopacs" >
                <DiagnoseChat />
              </Route>
              <Route exact path="/" >
                <DiagnoseChat />
              </Route>
              <Route path={chatLinks.ACCEPT_MEMBERSHIP}>
                <DiagnoseChat />
              </Route>
              <Route path={chatLinks.MEDSCRIBE_CONNECT}>
                <Suspense fallback={<PageLoading />}>
                  <MedscribeConnect />
                </Suspense>
              </Route>
              <Route path={MODAL_ROUTES} render={() => null} />
              {user?.reviewer?.status !== "approved" && (
                <Route path={chatLinks.REVIEWER_REGISTRATION} render={() => null} />
              )}
              <Route component={NotFound} />
            </Switch>
          </Suspense>

          {canShowDisclaimer && <DisclaimerPopup />}
        </main>

        <Switch>
          <Route path={chatLinks.LOGIN} >
            <ChatAuthModal>
              <Suspense fallback={<PageLoading />}>
                <LoginModalContent history={history} chatLinks={chatLinks} />
              </Suspense>
            </ChatAuthModal>
          </Route>

          <Route path={chatLinks.REGISTER} >
            <ChatAuthModal>
              <Suspense fallback={<PageLoading />}>
                <RegisterModalContent history={history} chatLinks={chatLinks} />
              </Suspense>
            </ChatAuthModal>
          </Route>

          <Route path={chatLinks.VERIFY_OTP} >
            <ChatAuthModal>
              <Suspense fallback={<PageLoading />}>
                <VerifyOTPModalContent history={history} chatLinks={chatLinks} />
              </Suspense>
            </ChatAuthModal>
          </Route>

          <Route path={chatLinks.FORGOT_PASSWORD} >
            <ChatAuthModal>
              <Suspense fallback={<PageLoading />}>
                <PasswordRecoveryFormLayout
                  history={history}
                  summaryRedirectLink={chatLinks.FORGOT_PASSWORD_SUMMARY}
                />
              </Suspense>
            </ChatAuthModal>
          </Route>

          <Route path={chatLinks.FORGOT_PASSWORD_SUMMARY} >
            <ChatAuthModal>
              <Suspense fallback={<PageLoading />}>
                <RecoverySummary
                  loginRedirectLink={chatLinks.LOGIN}
                  recoveryRedirectLink={chatLinks.FORGOT_PASSWORD}
                />
              </Suspense>
            </ChatAuthModal>
          </Route>

          {user?.reviewer?.status !== "approved" && (
            <Route path={chatLinks.REVIEWER_REGISTRATION} >
              <ChatRouteModal>
                <Suspense
                  fallback={(
                    <p>Loading...</p>
                  )}
                >
                  <ReportReviewerForm
                    successRedirectLink="/"
                  />
                </Suspense>
              </ChatRouteModal>
            </Route>
          )}

          <Route path={`${chatLinks.ACCEPT_MEMBERSHIP}/:token`}>
            <ChatRouteModal>
              <Suspense fallback={<PageLoading />}>
                <AcceptMembershipPage />
              </Suspense>
            </ChatRouteModal>
          </Route>

          <Route path={chatLinks.ACCOUNT} >
            <ChatRouteModal>
              <Suspense fallback={<PageLoading />}>
                <AccountSettings />
              </Suspense>
            </ChatRouteModal>
          </Route>

          <Route path={chatLinks.API_REQUEST} >
            <ChatRouteModal>
              <Suspense
                fallback={(
                  <p>Loading...</p>
                )}
              >
                <ApiRequest
                  successRedirectLink="/"
                />
              </Suspense>
            </ChatRouteModal>
          </Route>

          <Route path="*" >
            <Suspense fallback={<PageLoading />}>
              <UserCategorisationModal />
            </Suspense>
          </Route>
        </Switch>
      </div>
    </chatAuthContext.Provider>
  )
}

function getSafeRedirect() {
  const param = new URLSearchParams(window.location.search).get('redirect');
  return param?.startsWith('/') ? param : null;
}

/** Appends the redirect so it survives moving between login, register and the code step. */
function withRedirect(link, safeRedirect) {
  return safeRedirect ? `${link}?redirect=${encodeURIComponent(safeRedirect)}` : link;
}

function LoginModalContent({ history, chatLinks }) {
  const safeRedirect = getSafeRedirect();
  return (
    <LoginFormLayout
      history={history}
      registerLink={withRedirect(chatLinks.REGISTER, safeRedirect)}
      successRedirectLink={safeRedirect ?? "/"}
      verificationRedirectLink={
        safeRedirect
          ? `${chatLinks.VERIFY_OTP}?redirect=${encodeURIComponent(safeRedirect)}`
          : chatLinks.VERIFY_OTP
      }
      forgotPasswwordLink={chatLinks.FORGOT_PASSWORD}
    />
  );
}

function RegisterModalContent({ history, chatLinks }) {
  const safeRedirect = getSafeRedirect();
  return (
    <RegisterFormLayout
      history={history}
      loginLink={withRedirect(chatLinks.LOGIN, safeRedirect)}
      successRedirectLink={safeRedirect ?? "/"}
      verificationRedirectLink={withRedirect(chatLinks.VERIFY_OTP, safeRedirect)}
      forgotPasswwordLink={chatLinks.FORGOT_PASSWORD}
    />
  );
}

function VerifyOTPModalContent({ history, chatLinks }) {
  const safeRedirect = getSafeRedirect();
  return (
    <VerifyOTPFormLayout
      history={history}
      loginLink={chatLinks.LOGIN}
      successRedirectLink={safeRedirect ?? "/"}
    />
  );
}

const ChatAuthModal = forwardRef(({
  autoOpen = true,
  children,
  onClose,
  onCancel,
  ...props
}, ref) => {
  const authDialogRef = useRef(null)
  useImperativeHandle(ref, () => {
    return authDialogRef.current
  }, [])

  const { handleClose, handleCancel } = useRouteModal(authDialogRef, {
    autoOpen,
    onClose,
    onCancel
  })

  return (
    <dialog
      data-test="chat-auth-modal"
      className="chat-auth-modal"
      ref={authDialogRef}
      onClose={handleClose}
      onCancel={handleCancel}
      {...props}
    >
      <div className="chat-auth-modal__backdrop" onClick={() => authDialogRef.current?.close?.()}>
        <div className="chat-auth-modal__content" onClick={(e) => e.stopPropagation()}>
          <div className="chat-auth-modal__header">
            <button
              type="button"
              className="chat-auth-modal__close-btn"
              onClick={() => authDialogRef.current?.close?.()}
              aria-label="Close modal"
            >
              <IoMdClose />
            </button>
          </div>

          <div className="chat-auth-modal__body">
            {children}
          </div>
        </div>
      </div>
    </dialog>
  )
})

const ChatRouteModal = forwardRef(({
  autoOpen = true,
  children,
  onClose,
  onCancel,
  ...props
}, ref) => {
  const routeDialogRef = useRef(null)
  useImperativeHandle(ref, () => {
    return routeDialogRef.current
  }, [])

  const { handleClose, handleCancel } = useRouteModal(routeDialogRef, {
    autoOpen,
    onClose,
    onCancel
  })

  return (
    <dialog
      className="chat-route-modal"
      ref={routeDialogRef}
      onClose={handleClose}
      onCancel={handleCancel}
      {...props}
    >
      <div className="chat-route-modal__body">
        <div className="chat-route-modal__close-btn-wrapper">
          <button
            type="button"
            className="chat-route-modal__close-btn"
            onClick={() => routeDialogRef.current?.close?.()}
          >
            <IoMdClose />
          </button>
        </div>

        {children}
      </div>
    </dialog>
  )
})

/**
 * @param {React.RefObject<HTMLDialogElement|null>} modalRef 
 * @param {Object} options
 * @param {boolean} [options.autoOpen]

 * @param {(ev: *) => void} [options.onClose]
 * @param {(ev: *) => void} [options.onCancel]
 */
function useRouteModal(modalRef, { autoOpen = true, onClose, onCancel }) {
  const history = useHistory()

  useEffect(() => {
    if (!autoOpen) return
    modalRef.current?.showModal?.()
  }, [])

  /** @param {React.SyntheticEvent<HTMLDialogElement, Event>} ev */
  function handleClose(ev) {
    // Skip navigation when the dialog was closed temporarily for Paystack payment
    if (ev.target?.dataset?.paystackPending) return
    history.push("/")
    onClose?.(ev)
  }

  /** @param {React.SyntheticEvent<HTMLDialogElement, Event>} ev */
  function handleCancel(ev) {
    ev.preventDefault()
    onCancel?.(ev)
  }

  return {
    handleClose,
    handleCancel,
  }
}


function isAdmin(user) {
  if (user?.email?.endsWith("@minohealth.org")) return true
  return false
}

const PROMO_ADMIN_EMAIL = "minohealthai@gmail.com"

function isPromoAdmin(user) {
  return user?.email?.toLowerCase() === PROMO_ADMIN_EMAIL
}